import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { ADDRESS, ADDRESS_CITY_LINE, BUSINESS_SHORT_NAME, EMAIL, PHONE_DISPLAY } from '../../constants/business';

// Business info for receipt header
const BUSINESS_INFO = {
  name: BUSINESS_SHORT_NAME,
  address: ADDRESS.street,
  city: ADDRESS_CITY_LINE,
  phone: PHONE_DISPLAY,
  email: EMAIL,
};

interface LineItem {
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
}

interface Discounts {
  member_discount?: { percent: number; amount: number };
  pay_now_discount?: { percent: number; amount: number };
  price_adjustment?: { percent: number | null; amount: number; reason: string };
  credits_used?: { service_name: string; value: number }[];
  wallet_bonus?: { percent: number; amount: number };
}

interface Receipt {
  id: string;
  receipt_number: string;
  receipt_type: 'appointment' | 'wallet_topup';
  reference_id: string;
  payment_method: string;
  stripe_receipt_url: string | null;
  subtotal: number;
  discounts: Discounts;
  total_amount: number;
  line_items: LineItem[];
  customer_name: string | null;
  customer_email: string | null;
  created_at: string;
}

const ReceiptPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReceipt = async () => {
      if (!id) {
        setError('Receipt ID not provided');
        setIsLoading(false);
        return;
      }

      try {
        const { data, error: fetchError } = await supabase
          .from('receipts')
          .select('*')
          .eq('id', id)
          .single();

        if (fetchError) {
          console.error('Error fetching receipt:', fetchError);
          setError('Receipt not found');
          return;
        }

        setReceipt(data as Receipt);
      } catch (err) {
        console.error('Error:', err);
        setError('Failed to load receipt');
      } finally {
        setIsLoading(false);
      }
    };

    fetchReceipt();
  }, [id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/Chicago',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'card':
        return 'Credit/Debit Card';
      case 'wallet':
        return 'Wallet Balance';
      case 'credit':
        return 'Service Credits';
      case 'afterpay':
        return 'Afterpay';
      default:
        return method;
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#3A5E63]"></div>
      </div>
    );
  }

  if (error || !receipt) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <svg
              className="mx-auto h-16 w-16 text-red-400 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              {error || 'Receipt Not Found'}
            </h2>
            <p className="text-gray-600 mb-6">
              The receipt you're looking for doesn't exist or you don't have permission to view it.
            </p>
            <button
              onClick={() => navigate('/profile')}
              className="bg-[#3A5E63] text-white px-6 py-2 rounded hover:bg-[#2d4a4d] transition-colors"
            >
              Back to Profile
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Calculate total discounts
  const totalDiscounts =
    (receipt.discounts.member_discount?.amount || 0) +
    (receipt.discounts.pay_now_discount?.amount || 0) +
    (receipt.discounts.price_adjustment?.amount || 0) +
    (receipt.discounts.credits_used?.reduce((sum, c) => sum + c.value, 0) || 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Action Buttons - Hide when printing */}
        <div className="mb-4 flex justify-between items-center print:hidden">
          <button
            onClick={() => navigate('/profile')}
            className="text-[#3A5E63] hover:underline flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Profile
          </button>
          <button
            onClick={handlePrint}
            className="bg-[#3A5E63] text-white px-4 py-2 rounded hover:bg-[#2d4a4d] transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
              />
            </svg>
            Print Receipt
          </button>
        </div>

        {/* Receipt Card */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden print:shadow-none">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#3A5E63] to-[#4a7a80] text-white p-6 text-center">
            <h1 className="text-2xl font-serif font-semibold mb-1">{BUSINESS_INFO.name}</h1>
            <p className="text-sm opacity-90">{BUSINESS_INFO.address}</p>
            <p className="text-sm opacity-90">{BUSINESS_INFO.city}</p>
            <p className="text-sm opacity-90 mt-2">
              {BUSINESS_INFO.phone} | {BUSINESS_INFO.email}
            </p>
          </div>

          {/* Receipt Info */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-500">Receipt Number</p>
                <p className="font-mono font-semibold text-lg text-[#3A5E63]">
                  {receipt.receipt_number}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Date</p>
                <p className="text-gray-800">{formatDate(receipt.created_at)}</p>
              </div>
            </div>
            {receipt.customer_name && (
              <div className="mt-4">
                <p className="text-sm text-gray-500">Customer</p>
                <p className="text-gray-800">{receipt.customer_name}</p>
                {receipt.customer_email && (
                  <p className="text-sm text-gray-600">{receipt.customer_email}</p>
                )}
              </div>
            )}
          </div>

          {/* Line Items */}
          <div className="p-6 border-b border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-4">
              {receipt.receipt_type === 'wallet_topup' ? 'Wallet Top-Up' : 'Services'}
            </h3>
            <div className="space-y-3">
              {receipt.line_items
                .filter((item) => !item.description.startsWith('Bonus'))
                .map((item, index) => (
                  <div key={index} className="flex justify-between">
                    <div>
                      <p className="text-gray-800">{item.description}</p>
                      {item.quantity > 1 && (
                        <p className="text-sm text-gray-500">
                          {item.quantity} x {formatCurrency(item.unit_price)}
                        </p>
                      )}
                    </div>
                    <p className="text-gray-800">{formatCurrency(item.total)}</p>
                  </div>
                ))}
            </div>
          </div>

          {/* Totals Section */}
          <div className="p-6 bg-gray-50">
            <div className="space-y-2">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span>{formatCurrency(receipt.subtotal)}</span>
              </div>

              {/* Discounts */}
              {receipt.discounts.member_discount && (
                <div className="flex justify-between text-green-600">
                  <span>Member Discount ({receipt.discounts.member_discount.percent}%)</span>
                  <span>-{formatCurrency(receipt.discounts.member_discount.amount)}</span>
                </div>
              )}

              {receipt.discounts.pay_now_discount && (
                <div className="flex justify-between text-green-600">
                  <span>Pay Now Discount ({receipt.discounts.pay_now_discount.percent}%)</span>
                  <span>-{formatCurrency(receipt.discounts.pay_now_discount.amount)}</span>
                </div>
              )}

              {receipt.discounts.price_adjustment && (
                <div className="flex justify-between text-green-600">
                  <span>
                    Price Adjustment
                    {receipt.discounts.price_adjustment.percent
                      ? ` (${receipt.discounts.price_adjustment.percent}%)`
                      : ''}
                  </span>
                  <span>-{formatCurrency(receipt.discounts.price_adjustment.amount)}</span>
                </div>
              )}

              {receipt.discounts.credits_used && receipt.discounts.credits_used.length > 0 && (
                <div className="border-t border-gray-200 pt-2 mt-2">
                  <p className="text-sm text-teal-600 font-medium mb-1">Service Credits Applied:</p>
                  {receipt.discounts.credits_used.map((credit, idx) => (
                    <div key={idx} className="flex justify-between text-teal-600 text-sm">
                      <span>{credit.service_name}</span>
                      <span>-{formatCurrency(credit.value)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Wallet Bonus (for top-ups) */}
              {receipt.discounts.wallet_bonus && (
                <div className="flex justify-between text-blue-600">
                  <span>Bonus ({receipt.discounts.wallet_bonus.percent}%)</span>
                  <span>+{formatCurrency(receipt.discounts.wallet_bonus.amount)}</span>
                </div>
              )}

              {/* Total */}
              <div className="flex justify-between font-bold text-lg pt-3 mt-3 border-t border-gray-300">
                <span className="text-gray-800">Total Paid</span>
                <span className="text-[#3A5E63]">{formatCurrency(receipt.total_amount)}</span>
              </div>
            </div>
          </div>

          {/* Payment Method */}
          <div className="p-6 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-500">Payment Method</p>
                <p className="text-gray-800">{getPaymentMethodLabel(receipt.payment_method)}</p>
              </div>
              <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                Paid
              </div>
            </div>

            {/* Stripe Receipt Link */}
            {receipt.stripe_receipt_url && (
              <div className="mt-4 print:hidden">
                <a
                  href={receipt.stripe_receipt_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#3A5E63] hover:underline text-sm flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                  View Stripe Receipt
                </a>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 bg-[#F8F6F2] text-center">
            <p className="text-gray-600 text-sm">Thank you for visiting {BUSINESS_INFO.name}!</p>
            <p className="text-gray-500 text-xs mt-2">
              Questions? Contact us at {BUSINESS_INFO.email}
            </p>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            background: white !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ReceiptPage;
